import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  IconButton,
  Tooltip,
  Autocomplete,
  createFilterOptions,
  InputAdornment,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useActiveMemberProfiles } from '../../hooks/useActiveMemberProfiles';
import { memberGroupAPI, userAPI, memberProfileAPI, auditLogAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import PendingTransactionsDialog from './PendingTransactionsDialog';
import { MemberGroup, MemberProfile } from '../../types';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: string;
}

interface GroupFormData {
  groupName: string;
  groupType: 'family' | 'corporate';
  primaryContact: User | null;
  companyName: string;
  billingAddress: string;
  groupDiscountPercent: number;
  maxMembers: number | null;
  isActive: boolean;
  chargeToGroupPrimaryContact: boolean;
  selectedMembers: MemberProfile[];
}

const initialFormData: GroupFormData = {
  groupName: '',
  groupType: 'family',
  primaryContact: null,
  companyName: '',
  billingAddress: '',
  groupDiscountPercent: 0,
  maxMembers: null,
  isActive: true,
  chargeToGroupPrimaryContact: false,
  selectedMembers: []
};

const GroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<MemberGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [currentGroupId, setCurrentGroupId] = useState<number | null>(null);
  const [formData, setFormData] = useState<GroupFormData>(initialFormData);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [membersDialogOpen, setMembersDialogOpen] = useState<boolean>(false);
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  const [membersLoading, setMembersLoading] = useState<boolean>(false);

  // Audit history state
  const [auditHistoryDialogOpen, setAuditHistoryDialogOpen] = useState<boolean>(false);
  const [auditHistoryData, setAuditHistoryData] = useState<any[]>([]);
  const [auditHistoryLoading, setAuditHistoryLoading] = useState<boolean>(false);
  const [selectedGroupForHistory, setSelectedGroupForHistory] = useState<number | null>(null);

  // Filter and search states
  const [groupTypeFilter, setGroupTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Table states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<keyof MemberGroup>('groupName');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // State for member profiles dropdown
  const [users, setUsers] = useState<User[]>([]);
  const [usersSearchLoading, setUsersSearchLoading] = useState<boolean>(false);

  // Fetch all member profiles (not filtered by active status) for group member selection
  const { profiles: memberProfiles, loading: memberProfilesLoading, refetch: refetchMemberProfiles } = useActiveMemberProfiles({ activeOnly: false, sortByName: false });

  // Pending transactions dialog state
  const [pendingTransactionsDialogOpen, setPendingTransactionsDialogOpen] = useState<boolean>(false);
  const [pendingTransactionsData, setPendingTransactionsData] = useState<{
    totalAmount: number;
    transactionCount: number;
    members: Array<{
      memberId: string;
      memberName: string;
      transactionCount: number;
      totalAmount: number;
    }>;
  } | null>(null);
  const [pendingTransactionsLoading, setPendingTransactionsLoading] = useState<boolean>(false);
  const [originalChargeToGroup, setOriginalChargeToGroup] = useState<boolean>(false);
  const [pendingTransactionsMode, setPendingTransactionsMode] = useState<'enable-group-billing' | 'disable-group-billing' | 'add-members' | 'remove-members'>('enable-group-billing');
  const [originalMemberIds, setOriginalMemberIds] = useState<string[]>([]);

  // Fetch all groups
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await memberGroupAPI.getAll();
      setGroups(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load groups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
    fetchUsers();
    refetchMemberProfiles();
  }, []);

  // Fetch all users for dropdown
  const fetchUsers = async () => {
    setUsersSearchLoading(true);
    try {
      const response = await userAPI.getUsers();
      const userProfiles = response.data.filter((profile: any) => {
        if (profile.role === 'member') {
          return {
            id: profile.id,
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email,
            phone: profile.phone,
            role: profile.role
          }
        }
      });
      
      // Remove duplicates based on id (in case there are any)
      const uniqueUsers = userProfiles.filter((user: User, index: number, self: User[]) => 
        index === self.findIndex((u: User) => u.id === user.id)
      );
      
      setUsers(uniqueUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users for dropdown.');
    } finally {
      setUsersSearchLoading(false);
    }
  };

  // Fetch all member profiles for group member selection


  // Reset page when search term or filter changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm, groupTypeFilter]);

  // Handle sort
  const handleRequestSort = (property: keyof MemberGroup) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter, sort, and paginate groups
  const filteredAndSortedGroups = React.useMemo(() => {
    // First filter by group type
    let filtered = groups;
    if (groupTypeFilter !== 'all') {
      filtered = groups.filter(group => group.groupType === groupTypeFilter);
    }

    // Then filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(group => 
        group.groupName?.toLowerCase().includes(search) ||
        group.companyName?.toLowerCase().includes(search)
      );
    }

    // Then sort the filtered results
    return filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return order === 'asc' ? 1 : -1;
      if (bValue === undefined) return order === 'asc' ? -1 : 1;

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [groups, order, orderBy, searchTerm, groupTypeFilter]);

  // Paginate groups
  const paginatedGroups = filteredAndSortedGroups.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle form field changes for text inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle select field changes
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle numeric field changes
  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericValue = value === '' ? 0 : Number(value);
    
    if (name === 'maxMembers') {
      setFormData({
        ...formData,
        [name]: value === '' ? null : numericValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: numericValue
      });
    }
  };

  // Handle member selection changes
  const handleMemberSelectionChange = (event: any, newValue: MemberProfile[]) => {
    // Check if primary contact is still in the new member list
    let updatedPrimaryContact = formData.primaryContact;
    
    if (formData.primaryContact) {
      const primaryContactStillInGroup = newValue.some(
        member => member.userId === formData.primaryContact?.id
      );
      
      if (!primaryContactStillInGroup) {
        updatedPrimaryContact = null;
      }
    }
    
    setFormData({
      ...formData,
      selectedMembers: newValue,
      primaryContact: updatedPrimaryContact
    });
  };

  // Open dialog for creating a new group
  const handleOpenCreateDialog = () => {
    setFormData(initialFormData);
    setEditMode(false);
    setCurrentGroupId(null);
    setDialogOpen(true);
  };

  // Open dialog for editing an existing group
  const handleOpenEditDialog = async (group: MemberGroup) => {
    // Refresh member profiles first to get the latest group membership data
    await refetchMemberProfiles();
    await fetchUsers();
    
    // Fetch existing group members first
    let existingMembers: MemberProfile[] = [];
    try {
      const membersResponse = await memberGroupAPI.getMembers(group.id);
      
      // Try to match existing members with member profiles
      const rawMembers = membersResponse.data;
      existingMembers = rawMembers.map((member: any) => {
        // First try to find by member profile ID if available
        let matchingProfile = memberProfiles.find(p => p.id === member.id);
        
        // If not found, try to find by userId
        if (!matchingProfile) {
          matchingProfile = memberProfiles.find(p => p.userId === member.userId);
        }
        
        // If we found a matching profile, use it as base and override with member data
        if (matchingProfile) {
          return {
            ...matchingProfile,
            memberGroupId: group.id,
            groupRole: member.groupRole || matchingProfile.groupRole
          };
        }
        
        // Otherwise create from raw member data
        return {
          id: member.id,
          userId: member.userId,
          firstName: member.firstName,
          lastName: member.lastName,
          email: member.email,
          phone: member.phone || member.phoneNumber,
          status: member.status || member.membershipStatus || 'active',
          membershipTierId: member.membershipTierId || '',
          memberGroupId: group.id,
          groupRole: member.groupRole
        };
      });
    } catch (err) {
      console.error('Error fetching existing group members:', err);
      // Continue with empty members list if fetch fails
    }

    // Find the primary contact from the users list (after members are loaded)
    const primaryContact = group.primaryContactId 
      ? users.find((user: User) => user.id === group.primaryContactId) || null
      : null;

    setFormData({
      groupName: group.groupName,
      groupType: group.groupType,
      primaryContact: primaryContact,
      companyName: group.companyName || '',
      billingAddress: group.billingAddress || '',
      groupDiscountPercent: group.groupDiscountPercent,
      maxMembers: group.maxMembers ?? null,
      isActive: group.isActive,
      chargeToGroupPrimaryContact: group.chargeToGroupPrimaryContact ?? false,
      selectedMembers: existingMembers
    });
    
    // Store original chargeToGroupPrimaryContact value to detect changes
    setOriginalChargeToGroup(group.chargeToGroupPrimaryContact ?? false);
    
    // Store original member IDs to detect member changes
    setOriginalMemberIds(existingMembers.map(m => m.id));
    
    setEditMode(true);
    setCurrentGroupId(group.id);
    setDialogOpen(true);
  };

  // Close dialogs
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDeleteDialogOpen(false);
    setDeleteError(null);
    setMembersDialogOpen(false);
    setAuditHistoryDialogOpen(false);
  };

  // Handle submit for creating or updating a group
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.groupName.trim()) {
        setError('Group name is required');
        return;
      }

      if (formData.groupDiscountPercent < 0 || formData.groupDiscountPercent > 100) {
        setError('Group discount must be between 0 and 100');
        return;
      }

      if (formData.maxMembers !== null && formData.maxMembers < 1) {
        setError('Maximum members must be at least 1');
        return;
      }

      // Validate primary contact if provided
      if (formData.primaryContact) {
        // Check if primary contact is already primary of another group
        const existingGroup = groups.find(g => 
          g.primaryContactId === formData.primaryContact?.id && 
          g.id !== currentGroupId
        );
        
        if (existingGroup) {
          setError(`This user is already a primary contact for group: ${existingGroup.groupName}`);
          return;
        }
      }

      // Check if chargeToGroupPrimaryContact is being toggled from false to true
      if (editMode && currentGroupId && !originalChargeToGroup && formData.chargeToGroupPrimaryContact) {
        // Check for pending transactions before proceeding
        try {
          const pendingResponse = await memberGroupAPI.checkPendingTransactions(currentGroupId);
          if (pendingResponse.data.hasPendingTransactions) {
            // Show the pending transactions dialog
            setPendingTransactionsMode('enable-group-billing');
            setPendingTransactionsData(pendingResponse.data);
            setPendingTransactionsDialogOpen(true);
            return; // Stop here and wait for user decision
          }
        } catch (err) {
          console.error('Error checking pending transactions:', err);
          // Continue with the save if we can't check pending transactions
        }
      }

      // Check if chargeToGroupPrimaryContact is being toggled from true to false
      if (editMode && currentGroupId && originalChargeToGroup && !formData.chargeToGroupPrimaryContact) {
        // Check for pending transactions BILLED TO GROUP before proceeding
        try {
          const pendingResponse = await memberGroupAPI.checkPendingTransactions(currentGroupId, true);
          if (pendingResponse.data.hasPendingTransactions) {
            // Show the pending transactions dialog
            setPendingTransactionsMode('disable-group-billing');
            setPendingTransactionsData(pendingResponse.data);
            setPendingTransactionsDialogOpen(true);
            return; // Stop here and wait for user decision
          }
        } catch (err) {
          console.error('Error checking pending transactions:', err);
          // Continue with the save if we can't check pending transactions
        }
      }

      // Check if members are being added (only if group has chargeToGroupPrimaryContact enabled)
      if (editMode && currentGroupId && formData.chargeToGroupPrimaryContact) {
        const currentMemberIds = formData.selectedMembers.map(m => m.id);
        const addedMemberIds = currentMemberIds.filter(id => !originalMemberIds.includes(id));
        
        if (addedMemberIds.length > 0) {
          // Check for pending transactions for added members (individual-billed)
          try {
            const pendingResponse = await memberGroupAPI.checkPendingTransactionsForMembers(
              currentGroupId,
              addedMemberIds,
              false // Check for individual-billed transactions
            );
            if (pendingResponse.data.hasPendingTransactions) {
              // Show the pending transactions dialog
              setPendingTransactionsMode('add-members');
              setPendingTransactionsData(pendingResponse.data);
              setPendingTransactionsDialogOpen(true);
              return; // Stop here and wait for user decision
            }
          } catch (err) {
            console.error('Error checking pending transactions for added members:', err);
            // Continue with the save if we can't check pending transactions
          }
        }
      }

      // Check if members are being removed (only if group has chargeToGroupPrimaryContact enabled)
      if (editMode && currentGroupId && formData.chargeToGroupPrimaryContact) {
        const currentMemberIds = formData.selectedMembers.map(m => m.id);
        const removedMemberIds = originalMemberIds.filter(id => !currentMemberIds.includes(id));
        
        if (removedMemberIds.length > 0) {
          // Check for pending transactions for removed members (group-billed)
          try {
            const pendingResponse = await memberGroupAPI.checkPendingTransactionsForMembers(
              currentGroupId,
              removedMemberIds,
              true // Check for group-billed transactions
            );
            if (pendingResponse.data.hasPendingTransactions) {
              // Show the pending transactions dialog
              setPendingTransactionsMode('remove-members');
              setPendingTransactionsData(pendingResponse.data);
              setPendingTransactionsDialogOpen(true);
              return; // Stop here and wait for user decision
            }
          } catch (err) {
            console.error('Error checking pending transactions for removed members:', err);
            // Continue with the save if we can't check pending transactions
          }
        }
      }

      // Proceed with save
      await performGroupSave();
    } catch (err: any) {
      console.error('Error saving group:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to save group. Please try again.';
      setError(errorMessage);
    }
  };

  // Separate function to perform the actual save operation
  const performGroupSave = async () => {
    try {
      const submitData = {
        ...formData,
        primaryContactId: formData.primaryContact?.id || null,
        companyName: formData.companyName || undefined,
        billingAddress: formData.billingAddress || undefined,
        maxMembers: formData.maxMembers || undefined,
        memberProfileIds: formData.selectedMembers.map(m => m.id)
      };

      // Remove the primaryContact and selectedMembers objects from submitData since the API expects different format
      const { primaryContact, selectedMembers, ...apiData } = submitData;

      let groupId: number;

      if (editMode && currentGroupId) {
        // Update existing group with members atomically
        await memberGroupAPI.updateWithMembers(currentGroupId, apiData);
        groupId = currentGroupId;
      } else {
        // Create new group with members atomically
        const createResponse = await memberGroupAPI.create(apiData);
        groupId = createResponse.data.id;
        
        // If there are members, update the group with members atomically
        if (formData.selectedMembers.length > 0) {
          await memberGroupAPI.updateWithMembers(groupId, {
            memberProfileIds: formData.selectedMembers.map(m => m.id)
          });
        }
      }

      // Set success message only after all operations complete
      if (editMode) {
        setSuccess('Group updated successfully!');
      } else {
        setSuccess('Group created successfully!');
      }

      // Close dialog and refresh data
      setDialogOpen(false);
      
      // Wait for all data to refresh before showing success
      await Promise.all([
        fetchGroups(),
        refetchMemberProfiles(), // Refresh member profiles to reflect group changes
        fetchUsers() // Refresh users data as well
      ]);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err: any) {
      console.error('Error in performGroupSave:', err);
      throw err; // Re-throw to be caught by handleSubmit
    }
  };

  // Handle pending transactions dialog decision
  const handlePendingTransactionsDecision = async (reassign: boolean) => {
    setPendingTransactionsLoading(true);
    try {
      if (reassign && currentGroupId) {
        // Determine which API call to make based on the mode
        if (pendingTransactionsMode === 'enable-group-billing') {
          // Reassign pending transactions to group
          await memberGroupAPI.reassignPendingTransactions(currentGroupId);
        } else if (pendingTransactionsMode === 'disable-group-billing') {
          // Reassign pending transactions back to individuals
          await memberGroupAPI.reassignPendingTransactionsToIndividuals(currentGroupId);
        } else if (pendingTransactionsMode === 'add-members') {
          // Get the added member IDs
          const currentMemberIds = formData.selectedMembers.map(m => m.id);
          const addedMemberIds = currentMemberIds.filter(id => !originalMemberIds.includes(id));
          
          // Reassign transactions for added members to group
          await memberGroupAPI.reassignPendingTransactionsForMembers(
            currentGroupId,
            addedMemberIds,
            true // assignToGroup = true
          );
        } else if (pendingTransactionsMode === 'remove-members') {
          // Get the removed member IDs
          const currentMemberIds = formData.selectedMembers.map(m => m.id);
          const removedMemberIds = originalMemberIds.filter(id => !currentMemberIds.includes(id));
          
          // Reassign transactions for removed members back to individuals
          await memberGroupAPI.reassignPendingTransactionsForMembers(
            currentGroupId,
            removedMemberIds,
            false // assignToGroup = false
          );
        }
      }
      
      // Close the pending transactions dialog
      setPendingTransactionsDialogOpen(false);
      setPendingTransactionsData(null);
      
      // Now proceed with saving the group
      await performGroupSave();
    } catch (err: any) {
      console.error('Error handling pending transactions:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to process pending transactions.';
      setError(errorMessage);
    } finally {
      setPendingTransactionsLoading(false);
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (id: number) => {
    setCurrentGroupId(id);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  // Handle delete group
  const handleDelete = async () => {
    if (!currentGroupId) return;

    try {
      setDeleteError(null);
      await memberGroupAPI.delete(currentGroupId);
      setSuccess('Group deleted successfully!');
      setDeleteDialogOpen(false);
      
      // Wait for data refresh to complete
      await Promise.all([
        fetchGroups(),
        refetchMemberProfiles()
      ]);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
    } catch (err: any) {
      console.error('Error deleting group:', err);
      const errorMessage = err?.response?.data?.message || "Failed to delete group. Please try again.";
      setDeleteError(errorMessage);
    }
  };

  // Open members dialog
  const handleOpenMembersDialog = async (groupId: number) => {
    setCurrentGroupId(groupId);
    setMembersDialogOpen(true);
    setMembersLoading(true);
    
    try {
      const response = await memberGroupAPI.getMembers(groupId);
      setGroupMembers(response.data);
    } catch (err) {
      console.error('Error fetching group members:', err);
      setError('Failed to load group members.');
    } finally {
      setMembersLoading(false);
    }
  };

  // Open audit history dialog
  const handleOpenAuditHistory = async (groupId: number) => {
    setSelectedGroupForHistory(groupId);
    setAuditHistoryDialogOpen(true);
    setAuditHistoryLoading(true);
    
    try {
      const response = await auditLogAPI.getEntityAuditLogs('MemberGroup', groupId.toString());
      // Handle different response structures - could be array or object with data/logs property
      const logs = Array.isArray(response.data) 
        ? response.data 
        : response.data?.data || response.data?.logs || [];
      setAuditHistoryData(logs);
    } catch (err) {
      console.error('Error fetching audit history:', err);
      setError('Failed to load audit history.');
      setAuditHistoryData([]); // Set empty array on error
    } finally {
      setAuditHistoryLoading(false);
    }
  };

  // Helper function to get available primary contacts based on selected members
  const getAvailablePrimaryContacts = (): User[] => {
    if (formData.selectedMembers.length === 0) {
      return [];
    }
    
    // Get user IDs from selected members
    const selectedUserIds = formData.selectedMembers.map(m => m.userId);
    
    // Filter users to only include those in selected members
    return users.filter(user => selectedUserIds.includes(user.id));
  };

  // Helper function to format group type for display
  const formatGroupType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <AdminAccessControl>
      {/* Breadcrumb navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </RouterLink>
        <Typography color="text.primary">Groups</Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Group Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Create Group
        </Button>
      </Box>

      {/* Alerts for error and success messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Group Type</InputLabel>
          <Select
            value={groupTypeFilter}
            label="Group Type"
            onChange={(e) => setGroupTypeFilter(e.target.value)}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="family">Family</MenuItem>
            <MenuItem value="corporate">Corporate</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          placeholder="Search by group or company name..."
        />
      </Box>

      {/* Main content */}
      <Paper sx={{ width: '100%', mb: 4 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'groupName'}
                    direction={orderBy === 'groupName' ? order : 'asc'}
                    onClick={() => handleRequestSort('groupName')}
                  >
                    Group Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'groupType'}
                    direction={orderBy === 'groupType' ? order : 'asc'}
                    onClick={() => handleRequestSort('groupType')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>Primary Contact</TableCell>
                <TableCell>Company</TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'groupDiscountPercent'}
                    direction={orderBy === 'groupDiscountPercent' ? order : 'asc'}
                    onClick={() => handleRequestSort('groupDiscountPercent')}
                  >
                    Discount
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'isActive'}
                    direction={orderBy === 'isActive' ? order : 'asc'}
                    onClick={() => handleRequestSort('isActive')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {groups.length === 0 ? "No groups found. Click the 'Create Group' button to add one." : "No groups match your filters."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedGroups.map((group) => (
                  <TableRow
                    hover
                    key={group.id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenEditDialog(group)}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {group.groupName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatGroupType(group.groupType)}
                        color={group.groupType === 'corporate' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const primaryContact = users.find(user => user.id === group.primaryContactId);
                        if (!primaryContact) {
                          return '-';
                        }
                        
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {group.chargeToGroupPrimaryContact && (
                              <AccountBalanceIcon
                                fontSize="small"
                                sx={{ color: 'rgba(0, 0, 0, 0.38)' }}
                                titleAccess="Charge to Primary Contact"
                              />
                            )}
                            <Typography variant="body2">
                              {`${primaryContact.firstName} ${primaryContact.lastName}`}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {group.companyName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {group.groupDiscountPercent}%
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={group.isActive ? 'Active' : 'Inactive'}
                        color={group.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Members">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenMembersDialog(group.id);
                          }}
                          sx={{ mr: 1 }}
                        >
                          <PeopleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Audit History">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAuditHistory(group.id);
                          }}
                          sx={{ mr: 1 }}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Group">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDeleteDialog(group.id);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredAndSortedGroups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} disableEscapeKeyDown={true} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Group' : 'Create Group'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {editMode
              ? 'Update the group details below.'
              : 'Enter the details for the new group.'}
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                name="groupName"
                label="Group Name"
                value={formData.groupName}
                onChange={handleChange}
                fullWidth
                required
                margin="dense"
              />
            </Grid>
            
           
            
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                options={memberProfiles.filter(profile => {
                  // const isActive = profile.status === 'active';
                  const isNotInAnyGroup = !profile.memberGroupId;
                  const isInCurrentGroup = profile.memberGroupId === currentGroupId;
                  return (isNotInAnyGroup || isInCurrentGroup);
                })}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                value={formData.selectedMembers}
                onChange={handleMemberSelectionChange}
                loading={memberProfilesLoading}
                filterOptions={createFilterOptions({
                  matchFrom: 'any',
                  stringify: (option) => `${option.firstName} ${option.lastName} ${option.email}`,
                })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Group Members"
                    margin="dense"
                    helperText="Search and select members to add to this group"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {memberProfilesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={`${option.firstName} ${option.lastName}`}
                      {...getTagProps({ index })}
                      key={option.id}
                      size="small"
                      color="primary"
                    />
                  ))
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">
                        {option.firstName} {option.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email} {option.phone ? `• ${option.phone}` : ''} • {option.status}
                        {option.memberGroupId && option.memberGroupId !== currentGroupId ? 
                          ' • Already in another group' : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                clearOnBlur
                selectOnFocus
                handleHomeEndKeys
                ChipProps={{ size: 'small' }}
              />
            </Grid>
             <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="group-type-label">Type</InputLabel>
                <Select
                  labelId="group-type-label"
                  id="group-type"
                  name="groupType"
                  value={formData.groupType}
                  label="Type"
                  onChange={handleSelectChange}
                >
                  <MenuItem value="family">Family</MenuItem>
                  <MenuItem value="corporate">Corporate</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={getAvailablePrimaryContacts()}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                value={formData.primaryContact}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    primaryContact: newValue
                  });
                }}
                loading={usersSearchLoading}
                disabled={formData.selectedMembers.length === 0}
                filterOptions={createFilterOptions({
                  matchFrom: 'any',
                  stringify: (option) => `${option.firstName} ${option.lastName} ${option.email}`,
                })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Primary Contact"
                    margin="dense"
                    helperText={
                      formData.selectedMembers.length === 0 
                        ? "Add members to the group first" 
                        : "Select a user from group members as primary contact"
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {usersSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText="No members selected"
                renderOption={(props, option) => {
                  // Check if this user is primary of another group
                  const isPrimaryOfOtherGroup = groups.find(g => 
                    g.primaryContactId === option.id && 
                    g.id !== currentGroupId
                  );
                  
                  return (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography variant="body1">
                          {option.firstName} {option.lastName}
                          {isPrimaryOfOtherGroup && (
                            <Chip 
                              label={`Primary of ${isPrimaryOfOtherGroup.groupName}`} 
                              size="small" 
                              color="warning" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.email} {option.phone ? `• ${option.phone}` : ''} • {option.role}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                clearOnBlur
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>

            {formData.groupType === 'corporate' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  name="companyName"
                  label="Company Name"
                  value={formData.companyName}
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                />
              </Grid>
            )}
            
            <Grid size={{ xs: 12 }}>
              <TextField
                name="billingAddress"
                label="Billing Address"
                value={formData.billingAddress}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                margin="dense"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="groupDiscountPercent"
                label="Group Discount (%)"
                value={formData.groupDiscountPercent}
                onChange={handleNumericChange}
                fullWidth
                margin="dense"
                type="number"
                inputProps={{ min: 0, max: 100 }}
                helperText="Discount percentage for group members"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                name="maxMembers"
                label="Maximum Members"
                value={formData.maxMembers || ''}
                onChange={handleNumericChange}
                fullWidth
                margin="dense"
                type="number"
                inputProps={{ min: 1 }}
                helperText="Leave empty for unlimited"
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.chargeToGroupPrimaryContact}
                    onChange={handleChange}
                    name="chargeToGroupPrimaryContact"
                  />
                }
                label="Charge all memberships to Primary Contact?"
                sx={{ mt: 2 }}
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
                When enabled, the primary contact's payment method will be used for all group member charges.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleChange}
                    name="isActive"
                  />
                }
                label="Active"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        disableEscapeKeyDown={true}
        aria-labelledby="alert-dialog-title"
        aria-describedBy="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this group? This action cannot be undone and will only succeed if there are no members in the group.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Members Dialog */}
      <Dialog
        open={membersDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        aria-labelledby="members-dialog-title"
      >
        <DialogTitle id="members-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <GroupsIcon sx={{ mr: 1 }} />
            Group Members
          </Box>
        </DialogTitle>
        <DialogContent>
          {membersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : groupMembers.length === 0 ? (
            <Typography align="center" sx={{ p: 2 }}>
              No members found in this group.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        {member.firstName && member.lastName ? (
                          <Typography
                            variant="body2"
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': {
                                color: 'primary.dark'
                              }
                            }}
                            onClick={() => {
                              // Try to find member profile first, then fall back to user
                              const memberProfile = memberProfiles.find(profile => 
                                profile.userId === member.userId || profile.id === member.id
                              );
                              
                              if (memberProfile) {
                                navigate(`/member-profiles/${memberProfile.id}`);
                              } else if (member.userId) {
                                navigate(`/users/${member.userId}`);
                              }
                            }}
                          >
                            {`${member.firstName} ${member.lastName}`}
                          </Typography>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>{member.email || 'N/A'}</TableCell>
                      <TableCell>{member.phone || member.phoneNumber || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={member.status || member.membershipStatus || 'Unknown'}
                          color={(member.status || member.membershipStatus) === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Audit History Dialog */}
      <Dialog
        open={auditHistoryDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        aria-labelledby="audit-history-dialog-title"
      >
        <DialogTitle id="audit-history-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1 }} />
            Audit History - Group {selectedGroupForHistory}
          </Box>
        </DialogTitle>
        <DialogContent>
          {auditHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : !Array.isArray(auditHistoryData) || auditHistoryData.length === 0 ? (
            <Typography align="center" sx={{ p: 2 }}>
              No audit history found for this group.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditHistoryData.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {log.userEmail || log.userId || 'System'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          size="small"
                          color={
                            log.action === 'CREATE' ? 'success' :
                            log.action === 'UPDATE' ? 'info' :
                            log.action === 'DELETE' ? 'error' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: '400px', wordWrap: 'break-word' }}>
                        {log.description}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Pending Transactions Dialog */}
      {pendingTransactionsData && (
        <PendingTransactionsDialog
          open={pendingTransactionsDialogOpen}
          onClose={() => {
            setPendingTransactionsDialogOpen(false);
            setPendingTransactionsData(null);
          }}
          onConfirm={handlePendingTransactionsDecision}
          groupName={formData.groupName}
          totalAmount={pendingTransactionsData.totalAmount}
          transactionCount={pendingTransactionsData.transactionCount}
          members={pendingTransactionsData.members}
          loading={pendingTransactionsLoading}
          mode={pendingTransactionsMode}
        />
      )}
      </AdminAccessControl>
    </Container>
  );
};

export default GroupsPage;
